import { useEffect, useMemo, useState } from "react"
import { Link, useParams } from "react-router-dom"
import { ALLOWED_ROLES_BY_ROUTE } from "../../common/constants"
import { If, Tabs } from "../../common/ui"
import { RequireRole } from "../../common/validation"
import {
  useFetchLoanOfficerQuery,
  useFetchPendingSubscriptionByIdQuery,
  useUpdatePendingSubscriptionMutation,
  useForcePricePendingSubscriptionMutation
} from "../../redux/queries"
import ManageDreamRatePanel from "../../common/ui/dream-rate/form/manage-panel"
import DreamRateActivityHistory from "../../common/ui/dream-rate/details/activity-history"
import DreamRateEmailHistory from "../../common/ui/dream-rate/details/email-history"
import { useToast } from "../../common/hooks"
import DreamRateDetailsHeader from "../../common/ui/dream-rate/details/header"
import { ForcePriceResponse, PendingSubscriptionFull } from "../../types"
import EngagementSummary from "../../common/ui/engagements/details/sections/summary"
import DreamRateQuoteHistory from "../../common/ui/dream-rate/details/quote-history"
import { SubscriptionDetailsSkeleton } from "../../common/ui/skeletons"
import { ArrowRight, CheckCircle } from "lucide-react"

const TABS = [
  { id: "summary", label: "Summary" },
  {
    id: "quote-history",
    label: "Quote history"
  },
  { id: "email-history", label: "Email history" },
  { id: "activity-history", label: "Activity history" }
]

type Params = {
  id?: string
}

const RateDropDetailsPage = () => {
  const { id } = useParams<Params>()
  const parsedId = useMemo(() => (id ? parseInt(id, 10) : undefined), [id])
  const { data, isFetching: isFetchingSubscriptionDetails } =
    useFetchPendingSubscriptionByIdQuery(
      { id: parsedId as number },
      { skip: !parsedId }
    )
  const { data: loDetails, isFetching: isFetchingLos } =
    useFetchLoanOfficerQuery(data?.loid ?? "", {
      skip: !data?.loid
    })

  const [updatePending] = useUpdatePendingSubscriptionMutation()
  const [forcePrice, forcePriceState] =
    useForcePricePendingSubscriptionMutation()
  const [panelOpen, setPanelOpen] = useState(false)
  const [tab, setTab] = useState<string>("summary")
  const { pushErrorToast, pushSuccessToast } = useToast()

  // Keep panel closed during refetch to prevent it from reopening
  useEffect(() => {
    if (isFetchingSubscriptionDetails && panelOpen) {
      setPanelOpen(false)
    }
  }, [isFetchingSubscriptionDetails, panelOpen])

  const cancel = async () => {
    if (!parsedId) return
    const resp = await updatePending({ id: parsedId, status: 2 })
    const payload: any = (resp as any)?.data
    if (payload?.status === "success") pushSuccessToast("Cancelled dream rate")
    else pushErrorToast("Failed to cancel dream rate")
  }

  const handleForcePrice = async () => {
    if (!parsedId) return
    const resp = await forcePrice({ id: parsedId })
    const result = (resp as any)?.data as ForcePriceResponse | undefined
    if (result?.status === "success" && result.subscription_id) {
      pushSuccessToast(
        `Priced at ${parseFloat(result.last_quote_rate!).toFixed(
          3
        )}% — converted to active subscription`
      )
    } else if (result?.status === "success" && result.last_quote_rate != null) {
      pushSuccessToast(
        `Priced at ${parseFloat(result.last_quote_rate).toFixed(3)}%`
      )
    } else if (result?.status === "success") {
      pushSuccessToast("Pricing complete — no qualifying rates found")
    } else {
      pushErrorToast("Failed to price dream rate")
    }
  }

  const isFetching = isFetchingLos || isFetchingSubscriptionDetails

  if (!parsedId) return null

  return (
    <RequireRole allowedRoles={ALLOWED_ROLES_BY_ROUTE["dream-rate"]}>
      <If
        condition={isFetching}
        then={<SubscriptionDetailsSkeleton />}
        elseCondition={!!(data && loDetails)}
        else={
          <div className="h-full flex flex-col">
            <div className="pt-6 bg-white">
              <div className="px-6">
                <DreamRateDetailsHeader
                  data={data as PendingSubscriptionFull}
                  bankName={loDetails?.bank}
                  onUpdateTrigger={() => setPanelOpen(true)}
                  onCancel={cancel}
                  onForcePrice={handleForcePrice}
                  isForcePricing={forcePriceState.isLoading}
                />
                {!!(data as PendingSubscriptionFull)?.subscription_id && (
                  <div className="mb-2 flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
                    <div className="flex items-center gap-2 text-emerald-800">
                      <CheckCircle size={18} />
                      <span className="text-sm font-medium">
                        This dream rate has been converted to an active
                        subscription
                      </span>
                    </div>
                    <Link
                      to={`/engagements/${
                        (data as PendingSubscriptionFull).subscription_id
                      }`}
                      className="flex items-center gap-1 text-sm font-medium text-emerald-700 hover:text-emerald-900"
                    >
                      View subscription <ArrowRight size={14} />
                    </Link>
                  </div>
                )}
              </div>
              <Tabs
                className="px-6"
                currentTab={tab}
                tabs={TABS}
                onChange={setTab}
              />
            </div>
            <div className="p-6 flex-1 bg-white">
              {tab === "summary" && (
                <EngagementSummary
                  data={data as PendingSubscriptionFull}
                  loEmail={loDetails?.email}
                />
              )}

              {tab === "quote-history" && (
                <DreamRateQuoteHistory subscriptionId={parsedId} />
              )}

              {tab === "activity-history" && (
                <DreamRateActivityHistory subscriptionId={parsedId} />
              )}

              {tab === "email-history" && (
                <DreamRateEmailHistory subscriptionId={parsedId} />
              )}
            </div>
            <ManageDreamRatePanel
              open={panelOpen}
              data={data}
              onClose={() => {
                setPanelOpen(false)
              }}
            />
          </div>
        }
      />
    </RequireRole>
  )
}

export default RateDropDetailsPage
